<script setup lang="ts">
const props = defineProps({
  flyerItem: Object,
  recipeId: {
    type: Number,
    default: 0,
  },
})
const emit = defineEmits(['listChange', 'dropdownOpen', 'dropdownClose'])

const userStore = useUserStore()
const config = useRuntimeConfig()
const router = useRouter()
const { maybeRequestReview } = useInAppReview()
const toast = useToast()

const showEditor = ref(false)
const editorIsLoading = ref(false)
const isSaving = ref(false)
const addToListLoading = ref(false)
const editorLists = ref(false)
const editorItem = ref(false)
const label = ref('')
const newListLabel = ref('')
const selectedListId = ref('new')
const showSubListForm = ref(false)

const user = computed(() => userStore.user)
const groceryItem = computed(() => user?.value?.grocery_list_items?.[props.flyerItem.id])

async function updateQuantity(action: 'plus' | 'minus' | 'delete') {
  const item = groceryItem.value
  if (!item)
    return

  const list_item_id = item.id
  if (action === 'plus')
    item.qty += 1
  else if (action === 'minus')
    item.qty -= 1
  else if (action === 'delete')
    delete user.value.grocery_list_items[props.flyerItem.id]

  try {
    editorIsLoading.value = true

    const response = await $fetch(`${config.public.apiUrl}/grocery-list/update-quantity`, {
      method: 'POST',
      params: {
        list_item_id,
        list_id: selectedListId.value,
        label: label.value,
        new_list_label: newListLabel.value,
        action,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    editorIsLoading.value = false

    if (response.success === 1) {
      showEditor.value = false
      user.value.grocery_list_items = response.data.lists
      emit('listChange')
    }
  }
  catch {
    editorIsLoading.value = false
  }
}

async function loadEditor() {
  try {
    editorIsLoading.value = true
    newListLabel.value = ''

    const response = await $fetch(`${config.public.apiUrl}/grocery-list/get-editor-data`, {
      method: 'GET',
      params: {
        flyer_item_id: props.flyerItem.id,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    editorIsLoading.value = false
    if (response.success == 1) {
      selectedListId.value = response.list_item.list_id
      editorLists.value = response.lists
      editorItem.value = response.list_item
      label.value = response.list_item.label
      showEditor.value = true
      showSubListForm.value = false
    }
  }
  catch {
    editorIsLoading.value = false
  }
}

async function saveListItem(newList = false) {
  try {
    isSaving.value = true

    const response = await $fetch(`${config.public.apiUrl}/grocery-list/save-editor-data`, {
      method: 'POST',
      params: {
        list_item_id: editorItem.value.id,
        list_id: (newList ? 'new' : selectedListId.value),
        label: label.value,
        new_list_label: newListLabel.value,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    isSaving.value = false
    if (response.success === 1) {
      showEditor.value = false
      showSubListForm.value = false
    }
  }
  catch {
    isSaving.value = false
  }
}

function handleEnter(event: KeyboardEvent) {
  event.preventDefault()
  saveListItem()
}

async function goToFlyer(item) {
  try {
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/get-flyer-data`, {
      method: 'GET',
      query: { item_id: item.id },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    if (response.success === 1) {
      router.push({
        name: 'FlyerPage',
        params: {
          id: response.flyer_id,
          slug: response.slug,
        },
        query: {
          item_id: response.flyer_item_id,
        },
      })
    }
    else if (response.success === 0) {
      console.error(response.message)
    }
  }
  catch (err) {
    console.error('Error getting flyer data:', err)
  }
}

async function addToShoppingList() {
  if (!user.value) {
    return
  }

  try {
    addToListLoading.value = true
    const response = await $fetch(`${config.public.apiUrl}/grocery-list/add-item-from-promo`, {
      method: 'GET',
      query: {
        item_id: props.flyerItem.id,
        recipe_id: props.recipeId,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    if (response.success) {
      user.value.grocery_list_items = response.lists
      emit('listChange')
      void maybeRequestReview('groceryAdd')
    }
    else {
      toast.add({
        title: 'Erreur',
        description: response.message || 'Impossible d’ajouter ce produit à votre liste d’épicerie.',
        color: 'error',
      })
    }
  }
  finally {
    addToListLoading.value = false
  }
}

const listItems = computed(() => {
  const items = [
    ...(editorLists.value?.length
      ? editorLists.value.map(list => ({
          label: list.label,
          type: 'checkbox' as const,
          checked: selectedListId.value === list.id,
          onUpdateChecked(checked: boolean) {
            if (checked)
              selectedListId.value = list.id
          },
          onSelect(e: Event) {
            e.preventDefault()
          },
        }))
      : [{
          label: 'Aucune sous-liste',
          icon: 'i-lucide-alert-triangle',
          type: 'label' as const,
        }]),
  ]

  // Add "Afficher dans la circulaire" option if item has flyer_item_id
  if (props.flyerItem.flyer_item_id) {
    items.unshift({
      label: 'Afficher dans la circulaire',
      icon: 'i-mdi-file-document-outline',
      onSelect: () => goToFlyer(props.flyerItem),
    })
  }

  return items
})

// Watch showEditor to emit events
watch(showEditor, (newValue) => {
  if (newValue) {
    emit('dropdownOpen')
  }
  else {
    emit('dropdownClose')
  }
})

onMounted(() => {
  showSubListForm.value = false
})
</script>

<template>
  <div>
    <div v-if="userStore.user && flyerItem.id in userStore.user.grocery_list_items" class="min-w-15 inline-flex">
    <div class="inline-flex relative flex items-center border-t-1 border-b-1 border-tomate-500 w-full justify-between rounded-full">
      <button v-if="userStore.user.grocery_list_items[flyerItem.id].qty > 1" :disabled="editorIsLoading || showEditor" class="w-6 h-6 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-poivre-200 bg-tomate-400 hover:bg-tomate-500 text-white disabled:bg-tomate-300 flex items-center justify-center" @click.prevent="updateQuantity('minus')">
        <UIcon name="i-mdi-minus" class="text-xs" />
      </button>
      <button v-else :disabled="editorIsLoading || showEditor" class="w-6 h-6 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-poivre-200 bg-red-600 hover:bg-red-700 text-white disabled:bg-tomate-300 flex items-center justify-center" @click="updateQuantity('delete')">
        <UIcon name="i-mdi-trash-can-outline" class="text-xs" />
      </button>
      <div class="flex items-center px-0.5">
        <UDropdownMenu v-model:open="showEditor" :modal="false" :items="listItems" :content="{ align: 'start' }" :ui="{ content: 'w-60', group: 'max-h-38 overflow-y-scroll' }">
          <div class="text-center font-bold text-sm text-poivre flex items-center justify-center cursor-pointer hover:text-tomate-600 transition-colors w-4" @click="loadEditor">
            <UIcon v-if="editorIsLoading" name="i-mdi-loading" class="text-xs animate-spin" />
            <span v-else>{{ userStore.user.grocery_list_items[flyerItem.id].qty }}</span>
          </div>
          <template #content-top>
            <UTextarea v-model="label" class="mx-0.5 mt-0.5" autoresize placeholder="Description du produit..." :rows="2" @keydown.enter="handleEnter" />
          </template>
          <template #content-bottom>
            <div v-if="showSubListForm" class="flex items-center gap-2 px-2 py-2 border-t-1 border-gray-300">
              <UButton icon="i-mdi-close" size="xs" color="gray" variant="ghost" @click="showEditor = false" />
              <UInput v-model="newListLabel" :loading="isSaving" :disabled="isSaving" size="lg" class="flex-1" placeholder="Entrez un nom..." :ui="{ trailing: 'pe-1' }" @keydown.enter="handleEnter()">
                <template #trailing>
                  <UButton
                    color="primary"
                    variant="ghost"
                    icon="i-mdi-content-save"
                    :disabled="isSaving"
                    @click="saveListItem(true)"
                  />
                </template>
              </UInput>
            </div>
            <div v-else class="flex items-center justify-between px-2 py-2 border-t-1 border-gray-300">
              <UButton icon="i-mdi-close" size="xs" color="gray" variant="ghost" @click="showEditor = false" />
              <div class="flex gap-2">
                <UButton icon="i-mdi-playlist-plus" label="Sous-liste" size="xs" color="secondary" @click="showSubListForm = true" />
                <UButton icon="i-mdi-content-save" label="Sauvegarder" size="xs" :loading="isSaving" @click="saveListItem(false)" />
              </div>
            </div>
          </template>
        </UDropdownMenu>
      </div>
      <button :disabled="editorIsLoading || showEditor" class="w-6 h-6 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-poivre-200 bg-tomate-400 hover:bg-tomate-500 text-white disabled:bg-tomate-300 flex items-center justify-center" @click.prevent="updateQuantity('plus')">
        <UIcon name="i-mdi-plus" class="text-xs" />
      </button>
    </div>
  </div>
  <div v-else>
    <UButton :loading="addToListLoading" icon="i-mdi-playlist-plus" size="sm" @click.stop.prevent="addToShoppingList" />
  </div>
  </div>
</template>
